import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  Plus, 
  Search,
  MoreVertical,
  Trash2,
  Send,
  Eye,
  PackageCheck,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  received: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200'
};

const statusIcons = {
  draft: <Clock className="h-3 w-3" />,
  sent: <Send className="h-3 w-3" />,
  partial: <PackageCheck className="h-3 w-3" />,
  received: <CheckCircle className="h-3 w-3" />,
  cancelled: <XCircle className="h-3 w-3" />
};

const PurchaseOrders = () => {
  const { currencySymbol } = useSettings();
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [formData, setFormData] = useState({ items: [] });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');

  // Source labels for unified POs
  const sourceLabels = {
    standalone: { label: 'Standalone', color: 'bg-gray-100 text-gray-700' },
    project: { label: 'Project', color: 'bg-blue-100 text-blue-700' },
    inventory: { label: 'Inventory', color: 'bg-purple-100 text-purple-700' }
  };

  useEffect(() => {
    fetchOrders();
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders`);
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/suppliers`);
      setSuppliers(res.data.filter(s => s.is_active));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/products`);
      setProducts(res.data.filter(p => p.is_active));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/procurement/purchase-orders`, formData);
      toast.success('Purchase order created');
      setShowDialog(false);
      setFormData({ items: [] });
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create PO');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/procurement/purchase-orders/${id}/status?status=${status}`);
      toast.success('Status updated');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this purchase order?')) {
      try {
        await axios.delete(`${API_URL}/api/procurement/purchase-orders/${id}`);
        toast.success('PO deleted');
        fetchOrders();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete');
      }
    }
  };

  const openNew = () => {
    setFormData({ items: [{ product_id: '', quantity: 1, unit_price: 0 }], tax: 0 });
    setShowDialog(true);
  };

  const openView = (order) => {
    setViewingOrder(order);
    setShowViewDialog(true);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-fill unit price from product
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].unit_price = product.cost_price;
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const filteredOrders = orders.filter(o => {
    const statusMatch = filterStatus === 'all' || o.status === filterStatus;
    const sourceMatch = filterSource === 'all' || (o.source || 'standalone') === filterSource;
    return statusMatch && sourceMatch;
  });

  const stats = {
    total: orders.length,
    draft: orders.filter(o => o.status === 'draft').length,
    pending: orders.filter(o => o.status === 'sent').length,
    totalValue: orders.reduce((sum, o) => sum + (o.total || 0), 0),
    // Source breakdown
    projectPOs: orders.filter(o => o.source === 'project').length,
    standalonePOs: orders.filter(o => !o.source || o.source === 'standalone').length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Orders</h1>
          <p className="text-muted-foreground">Create and manage purchase orders to suppliers.</p>
        </div>
        <Button onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> New PO</Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Total POs</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-slate-500">{stats.draft}</div><div className="text-sm text-muted-foreground">Draft</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{stats.pending}</div><div className="text-sm text-muted-foreground">Pending Delivery</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{currencySymbol} {stats.totalValue.toLocaleString()}</div><div className="text-sm text-muted-foreground">Total Value</div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="standalone">{sourceLabels.standalone.label}</SelectItem>
            <SelectItem value="project">{sourceLabels.project.label}</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">
          {stats.projectPOs > 0 && <Badge className="mr-2 bg-blue-100 text-blue-700">{stats.projectPOs} Project</Badge>}
          {stats.standalonePOs > 0 && <Badge className="bg-gray-100 text-gray-700">{stats.standalonePOs} Standalone</Badge>}
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PO Number</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Items</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrders.map((order) => {
              const source = order.source || 'standalone';
              return (
              <TableRow key={order.id}>
                <TableCell className="font-mono font-medium">{order.po_number}</TableCell>
                <TableCell>{order.supplier_name}</TableCell>
                <TableCell>
                  <Badge className={sourceLabels[source]?.color || 'bg-gray-100'}>
                    {sourceLabels[source]?.label || source}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{format(new Date(order.date), 'MMM d, yyyy')}</TableCell>
                <TableCell>{order.items?.length || 0} items</TableCell>
                <TableCell className="text-right font-mono font-bold">{currencySymbol} {order.total?.toLocaleString()}</TableCell>
                <TableCell>
                  <Badge className={`${statusColors[order.status]} gap-1`}>
                    {statusIcons[order.status]} {order.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openView(order)}><Eye className="h-4 w-4 mr-2" /> View Details</DropdownMenuItem>
                      {order.source === 'project' && order.reference_id && (
                        <DropdownMenuItem onClick={() => window.location.href = `/projects/${order.reference_id}`}>
                          <Eye className="h-4 w-4 mr-2" /> View Project
                        </DropdownMenuItem>
                      )}
                      {order.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handleUpdateStatus(order.id, 'sent')}><Send className="h-4 w-4 mr-2" /> Mark as Sent</DropdownMenuItem>
                      )}
                      {['draft', 'cancelled'].includes(order.status) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(order.id)}>
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );})}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <ShoppingCart className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No purchase orders found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create PO Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select value={formData.supplier_id || ''} onValueChange={v => setFormData({...formData, supplier_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Expected Delivery</Label>
                <Input type="date" value={formData.expected_date || ''} onChange={e => setFormData({...formData, expected_date: e.target.value})} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3 w-3 mr-1" /> Add Item</Button>
              </div>
              <div className="space-y-2 border rounded-lg p-3">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground">
                  <div className="col-span-5">Product</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-2">Unit Price</div>
                  <div className="col-span-2">Total</div>
                  <div className="col-span-1"></div>
                </div>
                {formData.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Select className="col-span-5" value={item.product_id} onValueChange={v => updateItem(idx, 'product_id', v)}>
                      <SelectTrigger className="col-span-5"><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="col-span-2" type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-2" type="number" step="0.01" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                    <div className="col-span-2 font-mono text-sm">{currencySymbol} {(item.quantity * item.unit_price).toFixed(2)}</div>
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(idx)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} />
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span className="font-mono">{currencySymbol} {calculateSubtotal().toFixed(2)}</span></div>
              <div className="flex justify-between text-sm items-center">
                <span>Tax</span>
                <Input type="number" className="w-24 text-right" value={formData.tax || 0} onChange={e => setFormData({...formData, tax: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span className="font-mono">{currencySymbol} {(calculateSubtotal() + (formData.tax || 0)).toFixed(2)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Create PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Purchase Order - {viewingOrder?.po_number}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Supplier:</span> <span className="font-medium">{viewingOrder?.supplier_name}</span></div>
              <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{viewingOrder?.date && format(new Date(viewingOrder.date), 'MMM d, yyyy')}</span></div>
              <div><span className="text-muted-foreground">Status:</span> <Badge className={statusColors[viewingOrder?.status]}>{viewingOrder?.status}</Badge></div>
              <div><span className="text-muted-foreground">Expected:</span> <span className="font-medium">{viewingOrder?.expected_date ? format(new Date(viewingOrder.expected_date), 'MMM d, yyyy') : '-'}</span></div>
            </div>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewingOrder?.items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">
                        <span className={item.received_quantity >= item.quantity ? 'text-emerald-600' : 'text-amber-600'}>
                          {item.received_quantity || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">{currencySymbol} {item.unit_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{currencySymbol} {item.total?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-end">
              <div className="w-64 space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{currencySymbol} {viewingOrder?.subtotal?.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Tax</span><span className="font-mono">{currencySymbol} {viewingOrder?.tax?.toLocaleString()}</span></div>
                <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span className="font-mono">{currencySymbol} {viewingOrder?.total?.toLocaleString()}</span></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
